import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Select from 'react-select'
import 'react-toastify/dist/ReactToastify.css'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import { API_BASE_URL } from '../../../config/api'

const roleOptions = [
  { value: 'hr', label: 'HR' },
  { value: 'hod', label: 'HOD' },
  {value: 'coo', label: 'COO'},
  {value: 'cos', label: 'COS'},
]

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '38px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

const COOEditEmployee = () => {
  const baseUrl = API_BASE_URL
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const employeeId = location.state?.employeeId || id

  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingDepartments, setFetchingDepartments] = useState(true)
  const [fetchingEmployee, setFetchingEmployee] = useState(true)
  const [form, setForm] = useState({
    code: '',
    first_name: '',
    last_name: '',
    department: null,
    secondary_department: null,
    national_id: '',
    role: null,
    position: '',
    occupation: '',
  })

  useEffect(() => {
    const fetchDepartments = async () => {
      setFetchingDepartments(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/departments/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        setDepartments(Array.isArray(response.data) ? response.data : [])
      } catch (err) {
        toast.error('Failed to fetch departments.')
      } finally {
        setFetchingDepartments(false)
      }
    }
    fetchDepartments()
  }, [baseUrl])

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(
          `${baseUrl}/api/employees/${employeeId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        )
        const emp = response.data.employee
        const roleValue = emp.user?.role
        const matchedRole =
          roleOptions.find((r) => r.value === roleValue) ||
          (roleValue ? { value: roleValue, label: roleValue.toUpperCase() } : null)

        setForm({
          code: emp.code || '',
          first_name: emp.first_name || '',
          last_name: emp.last_name || '',
          department: emp.department
            ? {
                value: emp.department,
                label: emp.department_name
                  ? `${emp.department_name}${emp.course_center_name ? ` (${emp.course_center_name})` : ''}`
                  : '',
              }
            : null,
          secondary_department: emp.secondary_department
            ? {
                value: emp.secondary_department,
                label: emp.secondary_department_name
                  ? `${emp.secondary_department_name}${emp.course_center_name ? ` (${emp.course_center_name})` : ''}`
                  : '',
              }
            : null,
          national_id: emp.national_id || '',
          role: matchedRole,
          position: emp.position || '',
          occupation: emp.occupation || '',
        })
      } catch (err) {
        toast.error('Failed to fetch employee data.')
      } finally {
        setFetchingEmployee(false)
      }
    }
    if (employeeId) fetchEmployee()
  }, [employeeId, baseUrl])

  const departmentOptions = departments.map((dep) => ({
    value: dep.id,
    label: dep.course_center_name
      ? `${dep.name} (${dep.course_center_name})`
      : dep.name,
  }))

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (selected, { name }) => {
    setForm({ ...form, [name]: selected })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const token = Cookies.get('token')
      await axios.put(
        `${baseUrl}/api/employees/${employeeId}/`,
        {
          code: form.code,
          first_name: form.first_name,
          last_name: form.last_name,
          department: form.department?.value,
          secondary_department: form.secondary_department?.value,
          national_id: form.national_id,
          role: form.role?.value,
          position: form.position,
          occupation: form.occupation,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      toast.success('Employee updated successfully!')
      navigate(-1)
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Failed to update employee.',
      )
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = Boolean(
    form.code &&
      form.first_name &&
      form.last_name &&
      form.department &&
      form.national_id &&
      form.role &&
      form.position &&
      form.occupation,
  )

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3500}
        hideProgressBar={false}
      />
      <div
        data-sidebar-skin="dark"
        data-header-skin="light"
        data-navbar-brand-skin="dark"
        data-sidebar-state="expand"
      >
        <Topbar />
        <Sidebar />

        <div className="page-wrapper">
          <div className="content pb-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
              <div>
                <h4 className="fw-bold mb-1">Edit Employee</h4>
                <p className="text-muted mb-0 fs-13">
                  Update employee information
                </p>
              </div>
              <Link to="/coo/employees" className="btn btn-light">
                <i className="ti ti-arrow-left me-1" />
                Back to Employees
              </Link>
            </div>

            {fetchingEmployee ? (
              <div className="card shadow-sm p-5 text-center">
                <div
                  className="spinner-border text-primary mx-auto mb-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mb-0 fs-13">Loading employee data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="card shadow-sm mb-4">
                  <div className="card-header">
                    <h5 className="fw-bold mb-0">Employee Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label htmlFor="code" className="form-label">
                          Employee Code <span className="text-danger">*</span>
                        </label>
                        <input
                          id="code"
                          type="text"
                          name="code"
                          className="form-control"
                          value={form.code}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="first_name" className="form-label">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="first_name"
                          type="text"
                          name="first_name"
                          className="form-control"
                          value={form.first_name}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="last_name" className="form-label">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="last_name"
                          type="text"
                          name="last_name"
                          className="form-control"
                          value={form.last_name}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="role" className="form-label">
                          Role <span className="text-danger">*</span>
                        </label>
                        <Select
                          id="role"
                          name="role"
                          value={form.role}
                          onChange={handleSelectChange}
                          options={roleOptions}
                          placeholder="Select role"
                          classNamePrefix="react-select"
                          isDisabled={loading || fetchingEmployee}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="department" className="form-label">
                          Primary Department{' '}
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          id="department"
                          name="department"
                          value={form.department}
                          onChange={handleSelectChange}
                          options={departmentOptions}
                          placeholder={
                            fetchingDepartments
                              ? 'Loading departments...'
                              : 'Select primary department'
                          }
                          classNamePrefix="react-select"
                          isDisabled={loading || fetchingDepartments}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                        />
                      </div>

                      {form.role?.value === 'hod' && (
                        <div className="col-md-6">
                          <label
                            htmlFor="secondary_department"
                            className="form-label"
                          >
                            Secondary Department{' '}
                            <span className="text-muted fs-12">(Optional)</span>
                          </label>
                          <Select
                            id="secondary_department"
                            name="secondary_department"
                            value={form.secondary_department}
                            onChange={handleSelectChange}
                            options={departmentOptions}
                            placeholder={
                              fetchingDepartments
                                ? 'Loading departments...'
                                : 'Select secondary department'
                            }
                            classNamePrefix="react-select"
                            isDisabled={loading || fetchingDepartments}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                        </div>
                      )}

                      <div className="col-md-4">
                        <label htmlFor="national_id" className="form-label">
                          National ID <span className="text-danger">*</span>
                        </label>
                        <input
                          id="national_id"
                          type="text"
                          name="national_id"
                          className="form-control"
                          value={form.national_id}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="position" className="form-label">
                          Position <span className="text-danger">*</span>
                        </label>
                        <input
                          id="position"
                          type="text"
                          name="position"
                          className="form-control"
                          value={form.position}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="occupation" className="form-label">
                          Occupation <span className="text-danger">*</span>
                        </label>
                        <input
                          id="occupation"
                          type="text"
                          name="occupation"
                          className="form-control"
                          value={form.occupation}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                  <Link
                    to="/coo/employees"
                    className="btn btn-light"
                    onClick={(e) => loading && e.preventDefault()}
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !isFormValid}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Updating Employee...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-device-floppy me-1" />
                        Update Employee
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
            <p className="text-dark mb-0">
              2026 © <span className="link-primary">Casoe Technologies</span>,
              All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default COOEditEmployee
